import { Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { map } from 'rxjs';

import { ProductStoreSelector } from '@common/selectors/product.selector';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { ProductKeys } from '@config/keys/product.keys';
import { ModalScrollBlockBase } from '@core/base/modalScrollBlock/modal-scroll-block.base';
import { NativeService } from '@core/services/native/native.service';
import { GetModalInterface, ModalConfig } from '@core/services/modals/modals-util.type';
import { ModalsService } from '@core/services/modals/modals.service';
import { ButtonAtomComponent } from '@shared/atoms/button-atom/button-atom.component';
import { ModalFillerAtomComponent } from '@shared/atoms/modal-filler-atom/modal-filler-atom.component';
import { RoundedIconButtonComponent } from '@shared/atoms/rounded-icon-button/rounded-icon-button.component';
import { TagAtomComponent } from '@shared/atoms/tag-atom/tag-atom.component';

export interface UnavailableProductCard {
  id: string;
  name: string;
  imageUrl?: string;
}

export type UnavailableProductModalConfig = ModalConfig<{
  handle: typeof UnavailableProductModalComponent.handle;
  inputs: {
    products: UnavailableProductCard[];
  };
}>;

@Component({
  selector: 'app-unavailable-product-modal',
  templateUrl: './unavailable-product-modal.component.html',
  standalone: true,
  imports: [
    TranslateModule,
    ModalFillerAtomComponent,
    RoundedIconButtonComponent,
    TagAtomComponent,
    ButtonAtomComponent,
  ],
})
export class UnavailableProductModalComponent
  extends ModalScrollBlockBase
  implements GetModalInterface<UnavailableProductModalConfig>
{
  static handle = 'unavailable-product-modal' as const;

  readonly products = input<UnavailableProductCard[]>([]);

  private readonly modalSelector = inject(ModalStoreSelector);
  private readonly productSelector = inject(ProductStoreSelector);
  private readonly modalsService =
    inject<ModalsService<UnavailableProductModalConfig>>(ModalsService);
  private readonly settingsSelector = inject(SettingsStoreSelector);

  protected readonly cards = computed(() => {
    const catalog = this.productSelector.selectProducts();

    return this.products().map(product => {
      if (product.imageUrl) {
        return product;
      }

      const catalogProduct = catalog.find(item => item[ProductKeys.Id] === product.id);
      const images = catalogProduct?.[ProductKeys.Images];
      const imageUrl = images?.thumbnail?.url || images?.cover?.url;

      return imageUrl ? { ...product, imageUrl } : product;
    });
  });

  protected readonly isNotched = signal(false);
  protected readonly isKiosk = this.settingsSelector.deviceIsKiosk;
  protected readonly index = toSignal(
    this.modalSelector
      .selectModal(UnavailableProductModalComponent.handle)
      .pipe(map(modal => modal?.index ?? 4)),
    { initialValue: 4 },
  );

  constructor() {
    super();
    void NativeService.isNotched().then(notched => this.isNotched.set(notched));
  }

  close(): void {
    this.modalsService.close(UnavailableProductModalComponent.handle);
  }
}
